import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuPlus } from "react-icons/lu";
import { PageLayout } from "../../app/PageLayout";
import { Button, EmptyState, Field, Input, QtyStepper, SegmentedControl, Skeleton, Textarea } from "../../components/ui";
import { CatalogList } from "../../components/patterns/CatalogList";
import { KitList } from "../../components/patterns/KitList";
import { KitBuilder } from "../kits";
import { CompanySelectDialog } from "../../components/CompanySelectDialog/CompanySelectDialog";
import { useOrderDraft } from "../../context/OrderDraftContext";
import { plural, t } from "../../i18n";
import { ptBR } from "../../i18n/pt-BR";
import { CUSTOM, STANDARD } from "../procedures/useProcedureKits";
import { useOpmeFreeChoice } from "./useOpmeFreeChoice";
import { useOpmeKits } from "./useOpmeKits";
import { useOpmeSave } from "./useOpmeSave";
import { PATHS } from "../../app/paths";

const FREE = "escolha-livre";
const KITS = "pacotes";

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-body font-semibold text-fg-secondary">{children}</h2>
);

// Item fora do catálogo: nome digitado pelo médico e a quantidade.
const CustomOpmeForm: React.FC<{ initialName: string; onAdd: (name: string, quantity: number) => void; onCancel: () => void }> = ({ initialName, onAdd, onCancel }) => {
  const [name, setName] = useState(initialName);
  const [quantity, setQuantity] = useState(1);
  const [tried, setTried] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (name.trim()) onAdd(name.trim(), quantity);
  };

  return (
    <form onSubmit={submit} noValidate className="flex animate-rise flex-col gap-3 rounded-card border border-primary-line bg-primary-tint p-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t("opme.itemName")} error={tried && !name.trim() ? t("opme.itemNameMissing") : undefined} className="min-w-40 flex-1">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <QtyStepper value={quantity} onChange={setQuantity} label={name || t("opme.itemName")} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="text" onClick={onCancel}>{t("opme.cancel")}</Button>
        <Button variant="primary" size="sm" type="submit">{t("opme.add")}</Button>
      </div>
    </form>
  );
};

export const OpmePage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedButtonOpmeKits: mode, setSelectedButtonOpmeKits, setModificadoOpme, observacoes, setObservacoes } = useOrderDraft();
  const isFree = mode === FREE;
  const { save, saving, companySelection } = useOpmeSave();
  const free = useOpmeFreeChoice(save);
  const kits = useOpmeKits(!isFree, save);
  const [creating, setCreating] = useState(false);
  // null = formulário fechado; string = aberto com esse nome já preenchido.
  const [customName, setCustomName] = useState<string | null>(null);

  const goBack = () => {
    setModificadoOpme(false);
    navigate(PATHS.procedures);
  };

  const count = free.selected.length;
  const cta = isFree ? (
    <Button variant="primary" size="lg" loading={saving} disabled={!count} onClick={free.confirm}>
      {count ? plural(count, ptBR.opme.confirmCount) : t("procedures.confirm")}
    </Button>
  ) : (
    <Button variant="primary" size="lg" loading={saving} disabled={!kits.selectedKit} onClick={kits.confirm}>
      {kits.selectedKit ? t("procedures.confirmKit") : t("procedures.confirm")}
    </Button>
  );
  const ctaHint = isFree ? (count ? undefined : t("opme.freeMissing")) : kits.selectedKit?.name ?? t("procedures.kitMissing");

  return (
    <PageLayout title={t("opme.title")} onBack={goBack} nav={false} cta={cta} ctaHint={ctaHint}>
      <SegmentedControl
        aria-label={t("procedures.mode")}
        className="w-full max-w-sm self-center"
        value={isFree ? FREE : KITS}
        onChange={setSelectedButtonOpmeKits}
        options={[
          { value: KITS, label: t("procedures.kits"), count: kits.kits?.length || undefined },
          { value: FREE, label: t("procedures.free"), count: count || undefined },
        ]}
      />
      <p className="-mt-1.5 text-center text-caption text-fg-muted">{t(isFree ? "opme.freeHint" : "opme.kitsHint")}</p>

      {isFree ? (
        <>
          <SectionTitle>{t("opme.selected")}</SectionTitle>
          {count ? (
            <ul className="stagger flex flex-col gap-2.5">
              {free.selected.map((o) => (
                <li key={o.name} className="flex items-center gap-3 rounded-control border border-line bg-surface py-3 pl-4 pr-3 shadow-card">
                  <p className="min-w-0 flex-1 break-words text-label">{o.name}</p>
                  <QtyStepper value={o.quantity} onChange={(q) => free.setQuantity(o.name, q)} onRemove={() => free.remove(o.name)} label={o.name} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title={t("opme.emptyTitle")} description={t("opme.emptyHint")} />
          )}

          <SectionTitle>{t("opme.pick")}</SectionTitle>
          <CatalogList
            items={free.catalog?.map((name) => ({ id: name, label: name })) ?? null}
            addedIds={new Set(free.selected.map((o) => o.name))}
            onAdd={(entry) => free.add(entry.label)}
            searchLabel={t("opme.search")}
            placeholder={t("opme.searchPlaceholder")}
            empty={(query) => (
              <>
                {t("opme.notFound", { query })}{" "}
                <button type="button" className="font-semibold text-primary hover:underline" onClick={() => setCustomName(query.trim())}>
                  {t("opme.addAsOpme", { name: query.trim() })}
                </button>
              </>
            )}
          />

          {customName === null ? (
            <Button variant="text" className="self-start" onClick={() => setCustomName("")}>
              <LuPlus size={18} aria-hidden />
              {t("opme.addOwn")}
            </Button>
          ) : (
            <CustomOpmeForm
              key={customName}
              initialName={customName}
              onCancel={() => setCustomName(null)}
              onAdd={(name, quantity) => {
                free.add(name, quantity);
                setCustomName(null);
              }}
            />
          )}
        </>
      ) : (
        <>
          <SectionTitle>{t("opme.chooseKit")}</SectionTitle>
          <SegmentedControl
            aria-label={t("procedures.kitType")}
            tone="soft"
            value={kits.type}
            onChange={kits.changeType}
            options={[
              { value: STANDARD, label: t("procedures.standard") },
              { value: CUSTOM, label: t("procedures.custom") },
            ]}
          />
          <p className="rounded-control border-l-4 border-primary bg-primary-tint px-4 py-3 text-label text-fg-secondary">
            <strong className="text-fg">{t(kits.custom ? "procedures.customTitle" : "procedures.standardTitle")}</strong>{" "}
            {t(kits.custom ? "procedures.customText" : "procedures.standardText")} {t("procedures.aiText")}
          </p>

          {kits.kits === null ? (
            [0, 1].map((i) => <Skeleton key={i} className="h-32 rounded-card" />)
          ) : kits.kits.length ? (
            <KitList kits={kits.kits} selected={kits.selected} onSelect={kits.select} onRecommend={kits.recommend} recommending={kits.recommending} itemLabel={(o) => o.name} />
          ) : (
            <EmptyState
              title={t(kits.custom ? "procedures.noCustom" : "procedures.noStandard")}
              description={t(kits.custom ? "procedures.noCustomHint" : "procedures.noStandardHint")}
            />
          )}

          <Button variant="text" className="self-start" onClick={() => setCreating(true)}>
            <LuPlus size={18} aria-hidden />
            {t("procedures.newKit")}
          </Button>
          {creating && (
            <KitBuilder
              kind="opme"
              onClose={() => setCreating(false)}
              onCreated={(kind, name) => {
                setCreating(false);
                if (kind === "opme") kits.showCreated(name);
              }}
            />
          )}
        </>
      )}

      <Field label={t("opme.notes")} hint={t("opme.notesHint")}>
        <Textarea rows={3} maxLength={508} value={observacoes} onChange={(e) => setObservacoes(e.target.value)} placeholder={t("opme.notesPlaceholder")} />
      </Field>

      {companySelection.isPromptVisible && (
        <CompanySelectDialog
          companies={companySelection.companies}
          onConfirm={companySelection.handleConfirm}
          onClose={companySelection.handleClose}
        />
      )}
    </PageLayout>
  );
};
